import React, {Suspense, Fragment, useState, useEffect  } from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {FileUploadProvider} from '@common/uploads/uploader/file-upload-provider';
import {ArticleEditorTitle} from '@common/article-editor/article-editor-title';
import {ArticleEditorStickyHeader} from '@common/article-editor/article-editor-sticky-header';
import {useNavigate} from '@common/utils/hooks/use-navigate';
import {
  CreatePostPayload,
  useCreatePost,
} from '@app/blog/post/requests/use-create-post';
import {FullPageLoader} from '@common/ui/progress/full-page-loader';
import {StaticPageTitle} from '@common/seo/static-page-title';
import {Trans} from '@common/i18n/trans';
import {Footer} from '@common/ui/footer/footer';
import {DashboardNavbar} from '@app/dashboard/dashboard-navbar';
import {FormTextField} from '@common/ui/forms/input-field/text-field/text-field';
import {Form} from '@common/ui/forms/form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {Button} from '@common/ui/buttons/button';
import {FormImageSelector} from '@common/ui/images/image-selector';
const ArticleBodyEditor = React.lazy(
  () => import('@common/article-editor/article-body-editor'),
);
import {RichTextEditor} from '@common/ui/forms/richtexteditor';

import {FormNormalizedModelField} from '@common/ui/forms/normalized-model-field';
import { useParams } from 'react-router-dom';
import {BlogSidebar} from '@app/blog/blog-sidebar';
import {apiClient} from '@common/http/query-client';
import Select, { MultiValue } from 'react-select';
import {FormSelect, Option} from '@common/ui/forms/select/select';
import {Item} from '@common/ui/forms/listbox/item';

type CategoryOption = {
  value: number;
  label: string;
};

export function CreatePost() {
  const navigate = useNavigate();
  const createPost = useCreatePost();
  const form = useForm<CreatePostPayload>();
  const { site_id } = useParams();
  const { setValue, register, handleSubmit } = form;  
  const [availableCategories, setAvailableCategories] = useState<CategoryOption[]>([]);
  // Register 'content' with react-hook-form
  useEffect(() => {
    register('content', { required: true });
    register('project_id');
    setValue('project_id', site_id ? parseInt(site_id, 10) : undefined);
    apiClient.get(`categories/${site_id}?all=true`).then((response) => {
      const categories = response.data[0];
      setAvailableCategories(categories.map((cat: any) => ({ value: cat.id, label: cat.name })));
    });
  }, [register]);

  return (
    <Fragment>      
      <StaticPageTitle>
        <Trans message="Create Post" />
      </StaticPageTitle>
      <DashboardNavbar />
      <div className="flex h-full">
        {/* Sidebar Menu */}
        <BlogSidebar />
        {/* Main Content */}
        <div className="flex-1 p-6">
            <div className="p-12 md:p-24">
              <div className="mb-16 flex items-center justify-between">
                <h1 className="text-3xl font-light first:capitalize" id=":r3r:">Create New Post</h1>
                <Button
                 variant="flat"
                    color="primary"
                onClick={() => {
                  const formData = form.getValues();
                  const previewUrl = `sites/${site_id}/blog/preview`;

                  fetch(previewUrl, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formData),
                  })
                  .then(response => response.text())
                  .then(html => {
                    const newWindow = window.open('', '_blank');
                    
                    if (newWindow) {
                      newWindow.document.open();
                      newWindow.document.write(html);
                      newWindow.document.close();
                    }
                  });
                }}
              >
                Preview
              </Button>
              </div>
              <Form
              id="create-post"
              form={form}
              onSubmit={value => {                
                const payload = {
                                ...value,
                                categories: (value.categories || []).map((cat: any) => cat.value),
                                project_id: site_id ? parseInt(site_id, 10) : undefined, 
                            };
                        
                createPost.mutate(payload, {
                  onSuccess: response => {
                    close();
                    navigate(`/${site_id}/posts`, {
                      replace: true,
                    });
                  },
                });
              }}
            >
              <Fragment>
                <FormTextField
                  autoFocus
                  name="title"
                  label={<Trans message="Title" />}
                  className="mb-14"
                  required
                />
                <label htmlFor="categories">Categories</label>
           
                <Select
                  isMulti
                  name="categories"
                  options={availableCategories}
                  required
                  onChange={(selected: MultiValue<CategoryOption>) => {
                    // Map MultiValue to a regular array
                    const selectedCategories = selected.map((item) => ({
                      value: item.value,
                      label: item.label,
                    }));
                    setValue('categories', selectedCategories);
                  }}
                />  
                 <FormSelect className="mt-14" name="status" label="Status" selectionMode="single" required>
  <Item value="draft">Draft</Item>
  <Item value="published">Published</Item>
</FormSelect>  
                <div className="mt-14">
                  <label>Content</label>
                  <RichTextEditor
                    value={form.watch('content') || ''}
                    onChange={value => setValue('content', value)} // Sync RichTextEditor value with react-hook-form
                  />
                </div>

                <div className="mt-14">
                  <FileUploadProvider>
                    <FormImageSelector
                      name="featured_image"
                      diskPrefix="Blogs"
                      variant="square"
                      stretchPreview
                      label={<Trans message="Featured Image" />}
                      previewSize="w-100 h-100"
                      showRemoveButton
                    />
                  </FileUploadProvider>
                </div>
                <FormTextField
                  
                  name="meta_title"
                  label={<Trans message="Meta Title" />}
                  className="mb-14 mt-14"
                  required
                />
                <FormTextField
                  
                  name="meta_description"
                  label={<Trans message="Meta Description" />}
                  className="mb-14"
                  required
                />
                <FormTextField
                  
                  name="excerpt"
                  label={<Trans message="Excerpt" />}
                  className=""
                  inputElementType="textarea"
                  
                />
                <div className="text-muted">Max 200 charcters</div>
                <div className="mt-14">
                  <Button onClick={() => navigate(`/${site_id}/posts`)}>
                    <Trans message="Cancel" />
                  </Button>
                  <Button
                    form="create-post"
                    type="submit"
                    variant="flat"
                    color="primary"
                    disabled={createPost.isPending}
                  >
                    <Trans message="Create" />
                  </Button>
                </div>
              </Fragment>
            </Form>
          </div>
        </div>
      </div>
        
      <Footer className="mt-40 px-40" />        
    </Fragment>
  );
}
